import {
  Button,
  ButtonLevel,
  ButtonSize,
  Caption,
  CollapsibleDrawer,
  type CollapsibleDrawerProps,
  type CollapsibleDrawerTriggerProps,
  type Key,
  Tab,
  TabList,
  TabPanel,
  type TabPanelProps,
  TabSize,
  TabVariant,
  Tabs,
} from '@vizit/atoms';
import { XCloseIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import * as React from 'react';

interface ProductAssetsDrawerV2Props extends Omit<CollapsibleDrawerProps, 'title' | 'side'> {
  /**
   * The class name of the collapsible drawer content.
   */
  contentClassName?: string;
  /**
   * Custom header content to replace the default header.
   * If provided, the default title and close button will not be rendered.
   */
  headerContent?: React.ReactNode;
}

interface ProductAssetsDrawerV2ContentProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The key of the selected top-level tab (hero or carousel).
   */
  selectedKey?: Key | null;
  /**
   * The key of the default selected top-level tab.
   */
  defaultSelectedKey?: Key;
  /**
   * Handler that is called when the top-level selection changes.
   */
  onSelectionChange?: (key: Key | null) => void;
  /**
   * Content pinned to the left of the carousel panel's toolbar row (e.g., the
   * assets filter button).
   */
  carouselToolbarContent?: React.ReactNode;
  /**
   * Content pinned to the left of the hero panel's toolbar row (e.g., the
   * assets filter button).
   */
  heroToolbarContent?: React.ReactNode;
  /**
   * Content pinned to the right of the carousel panel's toolbar row (e.g., the inline "+ Upload" button).
   */
  carouselHeaderContent?: React.ReactNode;
  /**
   * Content pinned to the right of the hero panel's toolbar row (e.g., the inline "+ Upload" button).
   */
  heroHeaderContent?: React.ReactNode;
}

function ProductAssetsDrawerV2Trigger(props: CollapsibleDrawerTriggerProps) {
  return <CollapsibleDrawer.Trigger {...props} />;
}

function ProductAssetsDrawerV2HeroTabPanel(props: Omit<TabPanelProps, 'id'>) {
  const { children, className, ...other } = props;

  return (
    <TabPanel className={className} id="hero" {...other}>
      {children}
    </TabPanel>
  );
}

function ProductAssetsDrawerV2CarouselTabPanel(props: Omit<TabPanelProps, 'id'>) {
  const { children, className, ...other } = props;

  return (
    <TabPanel className={className} id="carousel" {...other}>
      {children}
    </TabPanel>
  );
}

interface ProductAssetsDrawerV2PanelToolbarProps {
  /**
   * Left-pinned toolbar content (e.g. the assets filter button).
   */
  toolbarContent?: React.ReactNode;
  /**
   * Right-pinned header content (e.g. the inline "+ Upload" button).
   */
  headerContent?: React.ReactNode;
}

/**
 * Toolbar row rendered above a panel's asset grid: filter actions pinned left,
 * upload action pinned right. `justify-between` keeps the upload action on the
 * panel's right edge, so dragging the drawer wider opens the gap between the
 * two groups instead of dragging the upload button along with the filter.
 */
function ProductAssetsDrawerV2PanelToolbar(props: ProductAssetsDrawerV2PanelToolbarProps) {
  const { toolbarContent, headerContent } = props;

  if (!toolbarContent && !headerContent) {
    return null;
  }

  return (
    <div className="flex items-center justify-between gap-2 w-full mb-3">
      <div className="flex items-center gap-2">{toolbarContent}</div>
      <div className="flex items-center gap-2">{headerContent}</div>
    </div>
  );
}

/**
 * V2 drawer content with updated layout per the V2 designs:
 * - Top-level Hero | Carousel tabs pinned to the top of the panel
 * - A toolbar row per panel: filter actions on the left, the "+ Upload" button
 *   on the right (the former Imported | Spark sub-tabs are replaced by the
 *   source filter inside the filter menu)
 * - Updated horizontal padding (V1 used `px-10`; V2 uses `pl-4 pr-2` to keep
 *   cards left-aligned with the labels above while trimming the right edge)
 */
function ProductAssetsDrawerV2Content(props: ProductAssetsDrawerV2ContentProps) {
  const {
    children,
    className,
    selectedKey,
    defaultSelectedKey,
    onSelectionChange,
    carouselToolbarContent,
    heroToolbarContent,
    carouselHeaderContent,
    heroHeaderContent,
    ...other
  } = props;

  const heroChild = React.Children.toArray(children).find((child) => {
    return React.isValidElement(child) && child.type === ProductAssetsDrawerV2HeroTabPanel;
  });

  const carouselChild = React.Children.toArray(children).find((child) => {
    return React.isValidElement(child) && child.type === ProductAssetsDrawerV2CarouselTabPanel;
  });

  // The panel children act as descriptors: they are never rendered directly.
  // Their props are lifted onto the replacement TabPanels below so caller
  // className and other props (test ids, aria attributes) are not dropped.
  // Render-function children are not supported: the panel content is composed
  // with the toolbar row, so children must be plain nodes.
  type PanelDescriptorProps = Omit<TabPanelProps, 'id' | 'children'> & { children?: React.ReactNode };
  const heroPanelProps = React.isValidElement(heroChild) ? (heroChild.props as PanelDescriptorProps) : undefined;
  const carouselPanelProps = React.isValidElement(carouselChild)
    ? (carouselChild.props as PanelDescriptorProps)
    : undefined;

  return (
    <div className={cx('flex flex-col w-full', className)} {...other}>
      <Tabs
        variant={TabVariant.Underline}
        size={TabSize.Sm}
        className="w-full"
        selectedKey={selectedKey}
        defaultSelectedKey={defaultSelectedKey}
        onSelectionChange={onSelectionChange}
      >
        <div className="flex items-end pt-2">
          {/* The Underline TabList atom defaults to `justify-center w-full`,
              which centers the tabs inside the available space. Override to
              `justify-start` so Hero/Carousel hug the left edge — flush with
              the ASSETS caption above and the asset cards below. */}
          <TabList className="flex justify-start gap-4 w-auto">
            <Tab className="!px-0" id="hero" key="hero">
              Hero
            </Tab>
            <Tab className="!px-0" id="carousel" key="carousel">
              Carousel
            </Tab>
          </TabList>
        </div>
        <TabPanel {...heroPanelProps} id="hero" className={cx('m-0 mt-6', heroPanelProps?.className)}>
          <ProductAssetsDrawerV2PanelToolbar toolbarContent={heroToolbarContent} headerContent={heroHeaderContent} />
          {heroPanelProps?.children}
        </TabPanel>
        <TabPanel {...carouselPanelProps} id="carousel" className={cx('m-0 mt-6', carouselPanelProps?.className)}>
          <ProductAssetsDrawerV2PanelToolbar
            toolbarContent={carouselToolbarContent}
            headerContent={carouselHeaderContent}
          />
          {carouselPanelProps?.children}
        </TabPanel>
      </Tabs>
    </div>
  );
}

/**
 * The width this drawer had before it became resizable, used as both the
 * opening width and the floor — it only ever expands from the layout it used to
 * have, never below it.
 *
 * V2 holds two `w-[118px]` asset cards separated by a `16px` column gap, sitting
 * inside a `pl-4 pr-2` panel (16px left / 8px right):
 *   `2×118 (cards) + 16 (gap-x-4) + 16 (pl-4) + 8 (pr-2) + 2 (slack) = 278px`
 * The asymmetric panel padding pulls the cards' right edge to ~10px from the
 * drawer edge while keeping the left edge at 16px — a tighter right-side trim
 * that visually matches the design intent.
 *
 * Unlike V1 this is a single value: the drawer used to carry a bespoke set of
 * width tiers here (a `278px` grid column >= 1440px, an absolutely-positioned
 * overlay between 1352–1439px, and a `33vh` stacked row below that), but the
 * panel itself was `278px` wide in both side-by-side tiers. The overlay tier is
 * now handled by the shared resize behavior in `CollapsibleDrawer`; the stacked
 * tier keeps its `1352px` boundary and `33vh` cap via `STACKED_TIER_CLASSES`
 * below.
 *
 * Passing this as both `defaultWidth` and `minWidth` means the drawer opens
 * *docked*: `CollapsibleDrawer` treats `width === minWidth` as in-flow, taking
 * a real `278px` grid column with no shadow, exactly as before. It only lifts
 * out of flow to overlay the page content once the user drags it wider than
 * this baseline.
 */
const BASELINE_WIDTH = 278;

/**
 * Below 1352px the drawer stops sitting beside the page content and drops to a
 * bottom drawer stacked under it, capped at a third of the viewport so the
 * product page above it stays the focus. This is this drawer's historical
 * breakpoint: a `278px` side panel next to content this dense stops earning its
 * column well above the shared atom's generic 821px stacking tier, so the
 * override widens that tier rather than replacing it — below 821px these same
 * rules simply keep winning over the atom's `grid-rows-2` default.
 *
 * Apply only while open — see `isOpenForLayout`. The closed variant stacks as
 * `grid-rows-1`, which this would otherwise replace just as readily.
 *
 * Must stay literal strings — Tailwind scans source text and cannot see class
 * names built from template literals. `V2_SIDE_BY_SIDE_MEDIA_QUERY` mirrors the
 * same boundary for the JS side (see `sideBySideMediaQuery` below).
 */
const STACKED_TIER_CLASSES = [
  '[@media(max-width:1351px)]:grid-cols-1',
  '[@media(max-width:1351px)]:grid-rows-[1fr_minmax(0,33vh)]',
  '[@media(max-width:1351px)]:grid-flow-row',
].join(' ');

/**
 * JS-side twin of `STACKED_TIER_CLASSES`' boundary, handed to
 * `CollapsibleDrawer` so its inline grid-column pin and resize handle disengage
 * at this drawer's stacking width instead of the shared 821px default — a
 * full-width bottom panel has nothing to resize against.
 */
const V2_SIDE_BY_SIDE_MEDIA_QUERY = '(min-width: 1352px)';

/**
 * 1px top border separating the stacked (bottom-drawer) panel from the page
 * content above it, in the semantic `border-primary` token. The shared atom
 * only draws its stacked top border below 821px — and in the lighter
 * `gray-utility-200` — while this drawer stacks below 1352px; the
 * `max-width:820px` variant overrides the atom's color (same Tailwind group
 * and breakpoint, so `cx`'s merge keeps this one) so the whole stacked tier
 * reads as a single border. Passed as `panelClassName`: it must land on the
 * panel element itself to merge against the atom's border rather than draw a
 * second one on the scroll container.
 */
const STACKED_PANEL_BORDER_CLASSES = [
  '[@media(max-width:1351px)]:border-t',
  '[@media(max-width:1351px)]:border-t-primary',
  '[@media(max-width:820px)]:border-t-primary',
].join(' ');

interface ProductAssetsDrawerV2DefaultHeaderProps {
  /**
   * Called when the user clicks the close button. When undefined, the close
   * button is not rendered (e.g. when the drawer is configured as `isDisabled`).
   */
  onClose?: () => void;
}

/**
 * Default V2 drawer header. Reuses the shared `Caption` atom so the styling —
 * uppercase mono `text-xs text-tertiary` with a hover info popup — stays in
 * lockstep with the page section captions like "YOUR HERO + CAROUSEL".
 *
 * `Caption`'s top separator rule is suppressed here: it divides sections within
 * a shared column, but this caption is the first thing in the drawer panel,
 * which already has its own border, so the rule reads as a stray hairline.
 */
function ProductAssetsDrawerV2DefaultHeader(props: ProductAssetsDrawerV2DefaultHeaderProps) {
  const { onClose } = props;

  return (
    <div className="pb-4">
      <Caption
        label="ASSETS"
        gap="gap-0"
        showSeparator={false}
        infoMetaData={{
          description: 'All product assets available for this PDP. Drag any asset onto the hero or carousel to use it.',
        }}
        trailingContent={
          onClose && (
            <Button
              onPress={onClose}
              aria-label="Close"
              size={ButtonSize.Sm}
              level={ButtonLevel.Ghost}
              iconStart={<XCloseIcon size={20} />}
            />
          )
        }
      >
        {/* Intentionally empty: Caption requires children */}
        <span aria-hidden="true" />
      </Caption>
    </div>
  );
}

function ProductAssetsDrawerV2(props: ProductAssetsDrawerV2Props) {
  const {
    children: childrenProp,
    contentClassName,
    headerContent,
    className,
    isOpen,
    defaultOpen,
    isDisabled,
    onOpenChange,
    ...other
  } = props;

  const children = React.Children.toArray(childrenProp).filter((child) => {
    return React.isValidElement(child) && child.type !== ProductAssetsDrawerV2Content;
  });

  const content = React.Children.toArray(childrenProp).find((child) => {
    return React.isValidElement(child) && child.type === ProductAssetsDrawerV2Content;
  });

  // `STACKED_TIER_CLASSES` has to be gated on the open state rather than applied
  // flat: it wins the `cx` merge against whichever stacked row template the base
  // variant picked, and the closed one is `grid-rows-1`, not `grid-rows-2`.
  // Applied unconditionally it would give a *closed* drawer a second row of up
  // to `33vh` below 1352px. Controlled callers pass `isOpen`; uncontrolled ones
  // fall back to `defaultOpen`, which is enough for the only consumer today
  // (`ProductDetailsTemplate` drives this controlled).
  const isOpenForLayout = isOpen ?? defaultOpen ?? false;

  // The shared `CollapsibleDrawer` ships a default header that uses
  // `text-lg semibold` styling. V2 wants the `Caption` look ("ASSETS" in mono
  // uppercase + separator rule). Provide a V2-flavored default; callers can
  // still override via `headerContent` (e.g. the original-view header).
  const resolvedHeaderContent = headerContent ?? (
    <ProductAssetsDrawerV2DefaultHeader onClose={isDisabled ? undefined : () => onOpenChange?.(false)} />
  );

  return (
    <CollapsibleDrawer
      title="Assets"
      side="right"
      contentWidth="custom"
      isOpen={isOpen}
      defaultOpen={defaultOpen}
      isDisabled={isDisabled}
      onOpenChange={onOpenChange}
      isResizable
      defaultWidth={BASELINE_WIDTH}
      minWidth={BASELINE_WIDTH}
      // No `resizeStorageKey`: the width is deliberately not persisted, so the
      // panel opens at its baseline every time the page is mounted.
      resizeHandleLabel="Resize assets panel"
      sideBySideMediaQuery={V2_SIDE_BY_SIDE_MEDIA_QUERY}
      className={cx(isOpenForLayout && STACKED_TIER_CLASSES, className)}
      {...other}
    >
      {children}
      <CollapsibleDrawer.Content
        className={cx('scrollbar-thin scrollbar-thumb-gray-utility-400 scrollbar-track-transparent', contentClassName)}
        panelClassName={STACKED_PANEL_BORDER_CLASSES}
        headerContent={resolvedHeaderContent}
      >
        {content}
      </CollapsibleDrawer.Content>
    </CollapsibleDrawer>
  );
}

export {
  ProductAssetsDrawerV2,
  ProductAssetsDrawerV2Content,
  ProductAssetsDrawerV2HeroTabPanel,
  ProductAssetsDrawerV2CarouselTabPanel,
  ProductAssetsDrawerV2Trigger,
};
export type { ProductAssetsDrawerV2Props, ProductAssetsDrawerV2ContentProps };
